import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  useTable,
  usePagination,
  useBlockLayout,
  useResizeColumns,
  useSortBy,
} from "react-table";
import { Link } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "../../../axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet";

import styles from "./Index.module.css";

import Breadcrumb from "../../../components/Breadcrumb/Breadcrumb";
import Button from "../../../components/Button/Button";
import Spinner from "../../../components/Spinner/Spinner";
import SearchBox from "../../../components/SearchBox/SearchBox";
import EditButton from "../../../components/EditButton/EditButton";
import Modal from "../../../components/Modal/Modal";
import FormikControl from "../../../components/FormikControl/FormikControl";

import AddNewIcon from "../../../assets/images/add-new-icon.svg";
import PrevIcon from "../../../assets/images/left-arrow-icon.svg";
import FirstIcon from "../../../assets/images/double-left-arrow-icon.svg";
import NextIcon from "../../../assets/images/right-arrow-icon.svg";
import LastIcon from "../../../assets/images/double-right-arrow-icon.svg";

const Table = ({
  columns,
  data,
  onSort,
  fetchData,
  pageCount: controlledPageCount,
  recordCount,
  startRecord,
  endRecord,
  setSearchKeyword,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        sortBy: [
          {
            id: "name",
            desc: false,
          },
        ],
      },
      manualPagination: true,
      manualSortBy: true,
      autoResetPage: false,
      autoResetSortBy: false,
      pageCount: controlledPageCount,
    },
    useSortBy,
    usePagination,
    useBlockLayout,
    useResizeColumns
  );

  useEffect(() => {
    onSort({ sortBy, pageIndex, pageSize });
    fetchData({ pageIndex, pageSize, sortBy });
  }, [fetchData, pageIndex, pageSize, onSort, sortBy]);

  const searchTable = (event) => {
    if (event.key === "Enter") {
      setSearchKeyword(event.target.value);
    }
  };

  return (
    <>
      <div className="TableHeader">
        <div className="SearchTable">
          <SearchBox
            placeholder="Search by Name"
            onKeyPress={(event) => searchTable(event)}
          />
        </div>
      </div>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render("Header")}
                  {/* Add a sort direction indicator */}
                  <span>
                    {column.isSorted
                      ? column.isSortedDesc
                        ? " 🔽"
                        : " 🔼"
                      : ""}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        {recordCount > 0 ? (
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td colSpan="10000">No records found!</td>
            </tr>
          </tbody>
        )}
      </table>
      <div className="TableFooter">
        <div className="TableRecordsCount">
          <div className="PageSize">
            <label>Show</label>
            <select
              className="FormControl"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
            >
              {[10, 20, 30, 40, 50, 100, 500].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
          </div>
          <div className="RecordsStartEnd">
            Showing {page.length} of {recordCount}
          </div>
        </div>
        <div className="TablePagination">
          <div className="PageCountStat">
            Page
            <span>
              {pageIndex + 1} of {pageCount}
            </span>
          </div>
          <div>|</div>
          <div className="GoToPage">
            <label>Go to page</label>
            <input
              className="FormControl"
              type="number"
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                gotoPage(page);
              }}
            />
          </div>
          <div className="PaginationNav">
            <Button
              type="button"
              btnType="Default"
              clicked={() => gotoPage(0)}
              disabled={!canPreviousPage}
            >
              <img src={FirstIcon} alt="First Page" />
            </Button>
            <Button
              type="button"
              btnType="Default"
              clicked={() => previousPage()}
              disabled={!canPreviousPage}
            >
              <img src={PrevIcon} alt="Previous Page" />
            </Button>
            <Button
              type="button"
              btnType="Default"
              clicked={() => nextPage()}
              disabled={!canNextPage}
            >
              <img src={NextIcon} alt="Next Page" />
            </Button>
            <Button
              type="button"
              btnType="Default"
              clicked={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
            >
              <img src={LastIcon} alt="Last Page" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

const Index = (props) => {
  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        width: 1050,
      },
      {
        Header: "",
        accessor: "id",
        Cell: ({ cell }) => [
          <EditButton clicked={() => handleEditForm(cell.row.values.id)} />,
        ],
        disableSortBy: true,
        width: 200,
      },
    ],
    []
  );

  // We'll start our table without any data
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [recordCount, setRecordCount] = useState(0);
  const [startRecord, setStartRecord] = useState(0);
  const [endRecord, setEndRecord] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState("");
  const fetchIdRef = useRef(0);

  const loadData = (pageSize, pageIndex, sortBy) => {
    let orderBy = {
      order: "name",
      by: "ASC",
    };

    if (sortBy.length) {
      orderBy = {
        order: sortBy[0].id,
        by: sortBy[0].desc ? "DESC" : "ASC",
      };
    }

    axios
      .get(
        "/profit-loss-types?per-page=" +
          pageSize +
          "&page=" +
          (pageIndex + 1) +
          "&keyword=" +
          searchKeyword +
          "&order=" +
          orderBy.order +
          "&by=" +
          orderBy.by
      )
      .then((response) => {
        setData(response.data);
        setPageCount(response.headers["x-pagination-page-count"]);
        setRecordCount(response.headers["x-pagination-total-count"]);
        setStartRecord(response.headers["x-pagination-start-record"]);
        setEndRecord(response.headers["x-pagination-end-record"]);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const [pageDetails, setPageDetails] = useState(null);

  const fetchData = useCallback(({ pageSize, pageIndex, sortBy }) => {
    const fetchId = ++fetchIdRef.current;

    setLoading(true);

    if (fetchId === fetchIdRef.current) {
      loadData(pageSize, pageIndex, sortBy);
    }

    setPageDetails({
      pageIndex: pageIndex,
      pageSize: pageSize,
      sortBy: sortBy,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = useCallback(() => {
    setLoading(true);
  }, []);

  const [formValues, setFormValues] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);

  const handleNewForm = () => {
    const formValues = {
      id: "",
      name: "",
    };

    setFormValues(formValues);
    setEditMode(false);
    setShowNewForm(!showNewForm);
  };

  const handleEditForm = (id) => {
    axios
      .get(`/profit-loss-types/${id}`)
      .then((response) => {
        const formValues = {
          id: response.data.id,
          name: response.data.name,
        };

        setFormValues(formValues);
        setEditMode(true);
        setShowNewForm(true);
      })
      .catch((error) => {
        if (error.response.data.code === 2001) {
          const errors = error.response.data.errors;
          toast.error(Object.values(errors[0])[0]);
        } else {
          toast.error(error.response.data.message);
        }
      });
  };

  const initialValues = {
    name: formValues ? formValues.name : "",
  };

  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Name is required")
      .trim()
      .max(50, "Name must contain only 50 characters"),
  });

  const onSubmit = (values) => {
    setLoading(true);

    const formData = {
      name: values.name,
    };

    if (!editMode) {
      axios
        .post("/profit-loss-types", formData)
        .then((response) => {
          toast.success(response.data.message);
          loadData(
            pageDetails.pageSize,
            pageDetails.pageIndex,
            pageDetails.sortBy
          );
          setShowNewForm(false);
        })
        .catch((error) => {
          if (error.response.data.code === 2001) {
            const errors = error.response.data.errors;
            toast.error(Object.values(errors[0])[0]);
          } else {
            toast.error(error.response.data.message);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      axios
        .put(`/profit-loss-types/${formValues.id}`, formData)
        .then((response) => {
          toast.success(response.data.message);
          loadData(
            pageDetails.pageSize,
            pageDetails.pageIndex,
            pageDetails.sortBy
          );
          setShowNewForm(false);
        })
        .catch((error) => {
          if (error.response.data.code === 2001) {
            const errors = error.response.data.errors;
            toast.error(Object.values(errors[0])[0]);
          } else {
            toast.error(error.response.data.message);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const newForm = showNewForm ? (
    <Modal
      show={showNewForm}
      modalClosed={handleNewForm}
      title={`${editMode ? "EDIT" : "ADD"}
        Profit Loss Type`}
    >
      <div className="FormContainer ModalForm">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          render={({ isSubmitting }) => {
            return (
              <Form>
                <div className={["FormBody", styles.FormBody].join(" ")}>
                  <FormikControl
                    control="input"
                    type="text"
                    label="Name *"
                    name="name"
                  />
                </div>
                <div className="FormActions">
                  <Button
                    btnType="Primary"
                    type="Submit"
                    disabled={isSubmitting}
                  >
                    Save
                  </Button>
                </div>
              </Form>
            );
          }}
        />
      </div>
    </Modal>
  ) : null;

  return (
    <React.Fragment>
      <Helmet>
        <title>Profit Loss Types | Lara Capital</title>
      </Helmet>
      <div className="PageHeader">
        <div className="ContentLeft">
          <div className="Title">
            <h1>Profit Loss Types</h1>
          </div>
          <Breadcrumb>
            <li>
              <Link to="/">Dashboard {showNewForm}</Link>
            </li>
            <li>
              <Link to="/profit-loss-types">Profit Loss TYpes</Link>
            </li>
          </Breadcrumb>
        </div>
        <div className="ContentRight">
          <Button btnType="Success" clicked={handleNewForm}>
            <img src={AddNewIcon} alt="New" /> <span>New</span>
          </Button>
        </div>
      </div>
      <div className="PageContent DataTable FixedWidth">
        <Table
          columns={columns}
          data={data}
          onSort={handleSort}
          fetchData={fetchData}
          loading={loading}
          pageCount={pageCount}
          recordCount={recordCount}
          startRecord={startRecord}
          endRecord={endRecord}
          setSearchKeyword={setSearchKeyword}
        />
      </div>
      <Spinner isLoading={loading} />
      {newForm}
      <ToastContainer />
    </React.Fragment>
  );
};

export default Index;
